/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

/**
 * Hivemind Orchestrator - Multi-peer LLM coordination
 */

import { smartChatCompletion, Message } from '@/providers/llm-provider'

export interface Persona {
  id: string
  name: string
  role: string
  provider: 'openai' | 'openrouter' | 'anthropic' | 'google' | 'xai'
  model: string
  systemPrompt: string
  color: string
  icon: string
}

export interface PeerResponse {
  personaId: string
  personaName: string
  content: string
  timestamp: number
  tokens?: number
  cost?: number
}

export interface JudgeResult {
  score: number
  reasoning: string
  timestamp: number
  tokens?: number
  cost?: number
}

export interface OrchestrationResult {
  peerResponses: PeerResponse[]
  judgeResult?: JudgeResult
  totalTokens: number
  totalCost: number
  latency: number
}

/**
 * Default peer personas
 */
export const DEFAULT_PERSONAS: Persona[] = [
  {
    id: 'gemini',
    name: 'Gemini_Reviewer',
    role: 'Technical Reviewer',
    provider: 'openrouter',
    model: 'google/gemini-2.5-pro',
    systemPrompt: `You are Gemini_Reviewer in the Hivemind system. Respond in MOTH format ONLY.

[CRITIQUE]
persona:Gemini_Reviewer
role:Technical Reviewer

[OVERALL_ASSESSMENT]
summary:<1-2 sentences>
current_score:<average of all scores below>

[SCORES]
correctness:{score=0.0-1.0; reason=<why>}
performance:{score=0.0-1.0; reason=<why>}
best_practices:{score=0.0-1.0; reason=<why>}

[STRENGTHS]
- <strength>

[WEAKNESSES]
- <weakness>

[RECOMMENDATIONS]
priority:high; category:technical; description:<what to fix>

[IMPROVEMENT_PATH]
to_reach_90_percent:
- <specific action to improve correctness score>
- <specific action to improve performance score>
- <specific action to improve best practices score>
estimated_score_after_changes:<0.0-1.0>

NO markdown. NO prose. MOTH format ONLY.`,
    color: 'purple',
    icon: 'Diamond',
  },
  {
    id: 'claude',
    name: 'Claude_Editor',
    role: 'Content Editor',
    provider: 'openrouter',
    model: 'anthropic/claude-sonnet-4-5',
    systemPrompt: `You are Claude_Editor in the Hivemind system. Respond in MOTH format ONLY.

[CRITIQUE]
persona:Claude_Editor
role:Content Editor

[OVERALL_ASSESSMENT]
summary:<1-2 sentences>
current_score:<average of all scores below>

[SCORES]
clarity:{score=0.0-1.0; reason=<why>}
structure:{score=0.0-1.0; reason=<why>}
readability:{score=0.0-1.0; reason=<why>}

[STRENGTHS]
- <strength>

[WEAKNESSES]
- <weakness>

[RECOMMENDATIONS]
priority:high; category:clarity; description:<what to fix>

[IMPROVEMENT_PATH]
to_reach_90_percent:
- <specific action to improve clarity score>
- <specific action to improve structure score>
- <specific action to improve readability score>
estimated_score_after_changes:<0.0-1.0>

NO markdown. NO prose. MOTH format ONLY.`,
    color: 'orange',
    icon: 'Shield',
  },
  {
    id: 'grok',
    name: 'Grok_Innovator',
    role: 'Innovation Advisor',
    provider: 'openrouter',
    model: 'x-ai/grok-4',
    systemPrompt: `You are Grok_Innovator in the Hivemind system. Respond in MOTH format ONLY.

[CRITIQUE]
persona:Grok_Innovator
role:Innovation Advisor

[OVERALL_ASSESSMENT]
summary:<1-2 sentences>
current_score:<average of all scores below>

[SCORES]
creativity:{score=0.0-1.0; reason=<why>}
innovation:{score=0.0-1.0; reason=<why>}
market_fit:{score=0.0-1.0; reason=<why>}

[STRENGTHS]
- <strength>

[WEAKNESSES]
- <weakness>

[RECOMMENDATIONS]
priority:high; category:innovation; description:<what to improve>

[IMPROVEMENT_PATH]
to_reach_90_percent:
- <specific action to improve creativity score>
- <specific action to improve innovation score>
- <specific action to improve market fit score>
estimated_score_after_changes:<0.0-1.0>

NO markdown. NO prose. MOTH format ONLY.`,
    color: 'green',
    icon: 'Rocket',
  },
]

/**
 * Judge persona - Using Claude 3.5 Sonnet (top-tier judge model)
 *
 * Research shows best judge models are:
 * - GPT-4 / Claude (proprietary, high accuracy)
 * - Llama 3.1 70B/405B (open-source, excellent reasoning)
 * - Qwen 2.5 7B/14B/72B (cost-efficient, competitive)
 * - DeepSeek (strong reasoning capabilities)
 */
export const JUDGE_PERSONA: Persona = {
  id: 'judge',
  name: 'Judge',
  role: 'Quality Assessor',
  provider: 'openrouter',
  model: 'anthropic/claude-3-5-sonnet-20241022',
  systemPrompt: `You are an impartial quality judge evaluating peer responses in the Hivemind system.

CRITICAL: Respond in MOTH format ONLY.

Your role:
1. Evaluate ALL peer responses against the rubric criteria
2. Consider technical accuracy, clarity, completeness, and innovation
3. Identify consensus and disagreements among peers
4. Provide objective, evidence-based scoring
5. Avoid bias toward any particular peer or approach
6. Provide clear path to reach 90 percent quality threshold

Required MOTH structure:
[JUDGE_SCORE]
packet_type:JUDGE_SCORE
timestamp:${new Date().toISOString()}

[OVERALL_SCORE]
score:0.0-1.0
confidence:0.0-1.0
gap_to_90_percent:<0.90 minus current score>

[EVALUATION]
consensus:<areas where peers agree>
disagreements:<areas where peers disagree>
strengths:<what all peers did well>
weaknesses:<what all peers missed or got wrong>

[PEER_SCORES]
gemini:{score=0.0-1.0; reasoning=<why>}
claude:{score=0.0-1.0; reasoning=<why>}
grok:{score=0.0-1.0; reasoning=<why>}

[REASONING]
<detailed explanation of overall score>
<evidence from peer responses>
<alignment with rubric criteria>

[RECOMMENDATIONS]
priority:high; description:<what to improve>
priority:medium; description:<what to consider>

[IMPROVEMENT_PATH]
to_reach_90_percent:
- <specific action 1 with expected score impact>
- <specific action 2 with expected score impact>
- <specific action 3 with expected score impact>
estimated_score_after_changes:<0.0-1.0>
estimated_effort:<low|medium|high>

NO markdown. NO prose. MOTH format ONLY.

Judge with high reasoning accuracy. Be thorough, fair, and evidence-based. Always provide actionable path to 90 percent threshold.`,
  color: 'yellow',
  icon: 'Scale',
}

/**
 * Send a prompt to a single peer
 */
async function queryPeer(
  persona: Persona,
  userPrompt: string,
  context?: string,
  conversationHistory?: Message[]
): Promise<PeerResponse> {
  const startTime = Date.now()

  const messages: Message[] = [
    { role: 'system', content: persona.systemPrompt },
  ]

  if (context) {
    messages.push({ role: 'system', content: `Context:\n${context}` })
  }

  // Add conversation history if provided (for context preservation)
  if (conversationHistory && conversationHistory.length > 0) {
    messages.push(...conversationHistory)
  }

  messages.push({ role: 'user', content: userPrompt })

  try {
    const response = await smartChatCompletion({
      model: persona.model,
      messages,
      temperature: 0.7,
      maxTokens: 8000, // Reasonable limit for peer responses
      stream: false,
    })

    const peerResponse = {
      personaId: persona.id,
      personaName: persona.name,
      content: response.content,
      timestamp: Date.now(),
      tokens: response.usage?.totalTokens,
      cost: response.usage?.cost || (response.usage ? calculateCost(response.usage.totalTokens, persona.model) : undefined),
    }

    console.log(`[Orchestrator] Peer ${persona.name} (${persona.model}):`, {
      tokens: peerResponse.tokens,
      cost: peerResponse.cost,
      hasUsage: !!response.usage,
      hasCost: !!response.usage?.cost
    })

    return peerResponse
  } catch (error) {
    console.error(`[Orchestrator] Error from peer ${persona.name} (${persona.model}):`, error)
    throw error
  }
}

/**
 * Send prompt to all peers in parallel
 */
export async function orchestratePeers(
  userPrompt: string,
  personas: Persona[] = DEFAULT_PERSONAS,
  context?: string,
  onPeerResponse?: (response: PeerResponse) => void,
  peerHistories?: Record<string, Message[]>
): Promise<PeerResponse[]> {
  const promises = personas.map(async (persona) => {
    try {
      // Get conversation history for this specific peer if available
      const conversationHistory = peerHistories?.[persona.id]
      const response = await queryPeer(persona, userPrompt, context, conversationHistory)
      if (onPeerResponse) {
        onPeerResponse(response)
      }
      return response
    } catch (error) {
      console.error(`Error querying ${persona.name}:`, error)
      return {
        personaId: persona.id,
        personaName: persona.name,
        content: `❌ Error: ${(error as Error).message}`,
        timestamp: Date.now(),
      }
    }
  })

  return Promise.all(promises)
}

/**
 * Judge evaluates all peer responses
 */
export async function judgeResponses(
  userPrompt: string,
  peerResponses: PeerResponse[],
  rubric?: string,
  judgeModel?: string
): Promise<JudgeResult> {
  const responseSummary = peerResponses
    .map((r) => `**${r.personaName}:**\n${r.content}`)
    .join('\n\n---\n\n')

  const judgePrompt = `
Original Request:
${userPrompt}

Peer Responses:
${responseSummary}

${rubric ? `Rubric:\n${rubric}\n\n` : ''}

Evaluate the quality and consensus of these responses.

CRITICAL: Respond in MOTH format ONLY. Use the [JUDGE_SCORE] packet structure defined in your system prompt.
`.trim()

  // Use custom judge model or default
  const judge = judgeModel
    ? { ...JUDGE_PERSONA, model: judgeModel }
    : JUDGE_PERSONA

  const messages: Message[] = [
    { role: 'system', content: judge.systemPrompt },
    { role: 'user', content: judgePrompt },
  ]

  const response = await smartChatCompletion({
    model: judge.model,
    messages,
    temperature: 0.3,
    maxTokens: 4000, // Judge responses should be concise
    stream: false,
  })

  // Parse MOTH format response
  const content = response.content

  // Extract score from MOTH format
  // Look for patterns like "score:0.85" or "score: 0.85"
  const scoreMatch = content.match(/score:\s*([0-9.]+)/i)
  const score = scoreMatch ? Number.parseFloat(scoreMatch[1]) : 0.5

  const judgeResult = {
    score,
    reasoning: content,
    timestamp: Date.now(),
    tokens: response.usage?.totalTokens,
    cost: response.usage?.cost || (response.usage ? calculateCost(response.usage.totalTokens, judge.model) : undefined),
  }

  console.log(`[Orchestrator] Judge - Score: ${score.toFixed(2)}, Tokens: ${judgeResult.tokens}, Cost: $${judgeResult.cost?.toFixed(6) || '0.000000'}`)

  return judgeResult
}

/**
 * Full orchestration: peers + judge
 */
export async function orchestrate(
  userPrompt: string,
  options: {
    personas?: Persona[]
    rubric?: string
    context?: string
    contextFiles?: string
    judgeModel?: string
    onPeerResponse?: (response: PeerResponse) => void
    onJudgeResult?: (result: JudgeResult) => void
    peerHistories?: Record<string, Message[]> // Conversation history for each peer
  } = {}
): Promise<OrchestrationResult> {
  const startTime = Date.now()

  // Combine context and contextFiles
  const fullContext = [options.context, options.contextFiles].filter(Boolean).join('\n\n')

  // Step 1: Query all peers in parallel
  const peerResponses = await orchestratePeers(
    userPrompt,
    options.personas,
    fullContext || undefined,
    options.onPeerResponse,
    options.peerHistories
  )

  // Step 2: Judge evaluates responses
  let judgeResult: JudgeResult | undefined
  try {
    judgeResult = await judgeResponses(
      userPrompt,
      peerResponses,
      options.rubric,
      options.judgeModel
    )
    if (options.onJudgeResult) {
      options.onJudgeResult(judgeResult)
    }
  } catch (error) {
    console.error('Judge error:', error)
  }

  // Calculate totals including judge
  const totalTokens = peerResponses.reduce((sum, r) => sum + (r.tokens || 0), 0) + (judgeResult?.tokens || 0)
  const totalCost = peerResponses.reduce((sum, r) => sum + (r.cost || 0), 0) + (judgeResult?.cost || 0)
  const latency = Date.now() - startTime

  console.log(`[Orchestrator] TOTALS - Tokens: ${totalTokens}, Cost: $${totalCost.toFixed(6)}, Latency: ${latency}ms`)

  return {
    peerResponses,
    judgeResult,
    totalTokens,
    totalCost,
    latency,
  }
}

/**
 * Estimate cost per 1K tokens (rough estimates)
 */
function calculateCost(tokens: number, model: string): number {
  const costPer1K: Record<string, number> = {
    'gpt-5': 0.03,
    'gpt-4o': 0.015,
    'claude-sonnet-4.5': 0.015,
    'claude-3.5-sonnet': 0.015,
    'gemini-pro-2.5': 0.01,
    'gemini-1.5-pro': 0.007,
    'grok-2': 0.02,
  }

  const modelKey = Object.keys(costPer1K).find((key) => model.includes(key))
  const rate = modelKey ? costPer1K[modelKey] : 0.01

  return (tokens / 1000) * rate
}

